import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import { Delete, Hotel, Person, Phone } from "@mui/icons-material";
import axios from "axios";
import MUITypography from "../../../Components/MUITypography";
import ReusableDataGrid from "../../../Components/ReusableDataGrid";
import { align_column, align_left, dataGridStyle, VM_Colors } from "../../../Styles/VMStyles";
import { displayFormattedDateTime, formatAmount, renderSNo } from "../../../Util/UtilHelper";
import { API_DELETE_ALLOCATED_ROOM } from "../../../Util/URLConstants";
import StorageFactory from "../../../Util/StorageFactory";
import { SESSION_USER_COUNTRY_DATA_KEY, SESSION_USER_ID_KEY } from "../../../Util/VMConstants";
import Loader from "../../../Components/Loader";

/** Lets grid cells shrink so text wraps to the next line instead of overflowing horizontally */
const cellRowWrapSx = {
  width: "100%",
  minWidth: 0,
  maxWidth: "100%",
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "flex-start",
  gap: 1,
  boxSizing: "border-box",
};

const cellTextWrapSx = {
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  lineHeight: 1.35,
  minWidth: 0,
  flex: "1 1 0%",
};

const RoomBilling = ({ bookedRoomsState = [], bookedEventState = [], displayHeading = true, readOnly = false }) => {
  // local merged state
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roomPage, setRoomPage] = useState(0);
  const [roomPageSize, setRoomPageSize] = useState(10);
  const [personPage, setPersonPage] = useState(0);
  const [personPageSize, setPersonPageSize] = useState(10);

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const currencySymbol = sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || "₹";

  // stable key generator
  const getRoomKey = (room) => {
    if (!room) return null;
    return String(room.id ?? room.room_booking_id ?? room.temp_id ?? room.tempKey ?? "");
  };

  // Merge incoming bookedRoomsState into local rooms (same as previous behavior)
  useEffect(() => {
    if (!Array.isArray(bookedRoomsState)) return;

    setRooms((prev) => {
      const map = new Map();
      prev.forEach((r) => {
        const key = getRoomKey(r);
        if (key) map.set(key, r);
      });

      bookedRoomsState.forEach((incoming) => {
        const key = getRoomKey(incoming);
        if (!key) {
          const temp = { ...incoming, temp_id: `temp_in_${Date.now()}_${Math.random()}` };
          map.set(temp.temp_id, temp);
        } else {
          map.set(key, incoming);
        }
      });

      const result = [];
      const seen = new Set();
      prev.forEach((r) => {
        const key = getRoomKey(r);
        if (key && map.has(key)) {
          result.push(map.get(key));
          seen.add(key);
        }
      });
      map.forEach((value, key) => {
        if (!seen.has(key)) result.push(value);
      });
      return result;
    });
  }, [bookedRoomsState]);

  const totalBookedAmount = rooms.reduce((acc, room) => acc + Number(room.booked_room_cost || 0), 0);

  const totalPersonCost = (Array.isArray(bookedEventState) ? bookedEventState : []).reduce((acc, event) => {
    const persons = event?.accomodation_persons_data?.data || [];
    const sum = persons.reduce((s, p) => s + (parseFloat(p.cost) || 0), 0);
    return acc + sum;
  }, 0);

  // Delete call (same behavior)
  const handleDeleteRoom = async (id) => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_DELETE_ALLOCATED_ROOM}?id=${id}&user_id=${userID}`);

      if (res.data?.status_code === 200) {
        setRooms((prev) => prev.filter((r) => {
          const key = getRoomKey(r);
          return key !== String(id);
        }));
      } else {
        console.error("Delete failed:", res.data?.message);
      }
    } catch (err) {
      console.error("Delete error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Prepare DataGrid rows for rooms (must have `id` field)
  const roomRows = rooms.map((r, idx) => ({
    s_no: idx + 1,
    id: getRoomKey(r) || `row_${idx}`,
    _raw: r,
    room_name: r.room_name || "N/A",
    room_type: r.room_type || "N/A",
    check_in: r.event_check_in,
    check_out: r.event_check_out,
    booked_room_cost: Number(r.booked_room_cost || 0),
    room_id: r.room_id ?? r.room_id,
    booking_id: r.id ?? r.room_booking_id ?? null,
  }));

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(roomRows.length / roomPageSize) - 1);
    if (roomPage > maxPage) setRoomPage(maxPage);
  }, [roomRows.length, roomPageSize, roomPage]);

  const gridCustomSx = useMemo(
    () => ({
      ...dataGridStyle,
      width: "100%",
      "& .MuiDataGrid-columnHeaderTitle": {
        whiteSpace: "normal",
        lineHeight: 1.2,
      },
      "& .MuiDataGrid-cell": {
        whiteSpace: "normal !important",
        lineHeight: "1.35 !important",
        overflow: "visible",
        display: "flex",
        alignItems: "flex-start",
        py: 1,
      },
      "& .MuiDataGrid-cell > *": {
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      },
      "& .MuiDataGrid-cellContent": {
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        textOverflow: "clip",
      },
    }),
    []
  );

  const roomColumns = [
        renderSNo(),

    {
      field: "room_name",
      headerName: "Room",
      flex: 1,
      // minWidth: 160,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={cellRowWrapSx}>
          <Hotel fontSize="small" color="primary" sx={{ flexShrink: 0, mt: 0.25 }} />
          <Typography variant="body2" sx={{ ...align_left, ...cellTextWrapSx }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },{
      field: "room_type",
      headerName: "Room Type",
      flex: 1.4,
      // minWidth: 160,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={cellRowWrapSx}>
          <Typography variant="body2" sx={{ ...align_left, ...cellTextWrapSx }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "check_in",
      headerName: "Check In",
      flex: 1,
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ ...align_left, ...cellTextWrapSx, width: "100%", fontWeight: 400 }}
        >
          {displayFormattedDateTime(params.value, currencySymbol, { wallClock: true })}
        </Typography>
      ),
    },
    {
      field: "check_out",
      headerName: "Check Out",
      flex: 1,
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ ...align_left, ...cellTextWrapSx, width: "100%", fontWeight: 400 }}
        >
          {displayFormattedDateTime(params.value, currencySymbol, { wallClock: true })}
        </Typography>
      ),
    },
    {
      field: "booked_room_cost",
      headerName: "Cost",
      flex: 0.8,
      // minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      // align: "right",
      // headerAlign: "right",
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ ...align_left, ...cellTextWrapSx, width: "100%", fontWeight: 600 }}
        >
          {formatAmount(params.value || 0, currencySymbol)}
        </Typography>
      ),
    },
    ...(!readOnly
      ? [
          {
            field: "actions",
            headerName: "Actions",
            flex: 0.5,
            minWidth: 80,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
              const bookingId = params.row.booking_id;
              return (
                <Tooltip title="Delete Room">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteRoom(bookingId ?? params.id)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              );
            },
          },
        ]
      : []),
  ];

  const personRows = useMemo(() => {
    const rows = [];
    if (Array.isArray(bookedEventState)) {
      bookedEventState.forEach((event, eIdx) => {
        const persons = event?.accomodation_persons_data?.data || [];
        persons.forEach((p, pIdx) => {
          rows.push({
            id: `e${eIdx}_p${pIdx}`,
            name: p.name || "N/A",
            phone: p.phone || "N/A",
            cost: Number(p.cost || 0),
          });
        });
      });
    }
    return rows;
  }, [bookedEventState]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(personRows.length / personPageSize) - 1);
    if (personPage > maxPage) setPersonPage(maxPage);
  }, [personRows.length, personPageSize, personPage]);

  const personColumns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.4,
      // minWidth: 180,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={cellRowWrapSx}>
          <Person fontSize="small" color="primary" sx={{ flexShrink: 0, mt: 0.25 }} />
          <Typography variant="body2" sx={{ ...align_left, ...cellTextWrapSx }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 1,
      minWidth: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={cellRowWrapSx}>
          <Phone fontSize="small" color="action" sx={{ flexShrink: 0, mt: 0.25 }} />
          <Typography variant="body2" sx={{ ...align_left, ...cellTextWrapSx }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "cost",
      headerName: "Cost",
      flex: 0.7,
      minWidth: 100,
      sortable: false,
      disableColumnMenu: true,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Typography variant="body2" sx={{ ...cellTextWrapSx, width: "100%", fontWeight: 600 }}>
          {formatAmount(params.value || 0, currencySymbol)}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {displayHeading && (
        <MUITypography variant="h3" sx={{ mb: 2 ,fontWeight: 700}}>
          Rooms Billing
        </MUITypography>
      )}

      <Box
        sx={{
          p: 2,
          border: "1px solid #e0e0e0",
          backgroundColor: VM_Colors.white,
          borderRadius: 2,
          ...align_column,
        }}
      >
        {/* Totals */}
        <Box sx={{ width: "100%", mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        {
            totalBookedAmount > 0 && (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color={VM_Colors.primary}>
              Total Booked Amount: {formatAmount(totalBookedAmount, currencySymbol)}
            </Typography>
          </Box>
          )}
          {
            totalPersonCost > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={VM_Colors.primary}>
                  Total Person Amount: {formatAmount(totalPersonCost, currencySymbol)}
                </Typography>
              </Box>
            )
          }
        </Box>

        {/* Rooms DataGrid */}
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <ReusableDataGrid
            rows={roomRows}
            columns={roomColumns}
            getRowId={(row) => row.id}
            paginationMode="client"
            rowCount={roomRows.length}
            page={roomPage}
            pageSize={roomPageSize}
            onPageChange={setRoomPage}
            onPageSizeChange={(n) => {
              setRoomPageSize(n);
              setRoomPage(0);
            }}
            pageSizeOptions={[5, 10, 20, 50]}
            autoHeight
            loading={loading}
            customSx={gridCustomSx}
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            disableColumnMenu
            hideFooterSelectedRowCount
          />
        </Paper>

        {/* Persons DataGrid (only if any persons) */}
        {personRows.length > 0 && (
          <Paper sx={{ width: "100%", overflow: "hidden", mt: 2 }}>
            <ReusableDataGrid
              rows={personRows}
              columns={personColumns}
              getRowId={(row) => row.id}
              paginationMode="client"
              rowCount={personRows.length}
              page={personPage}
              pageSize={personPageSize}
              onPageChange={setPersonPage}
              onPageSizeChange={(n) => {
                setPersonPageSize(n);
                setPersonPage(0);
              }}
              pageSizeOptions={[5, 10, 20]}
              autoHeight
              customSx={gridCustomSx}
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              disableColumnMenu
              hideFooterSelectedRowCount
            />
          </Paper>
        )}
      </Box>

      {loading && <Loader loading={loading} />}
    </Box>
  );
};

export default RoomBilling;
